import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Sparkles,
  Compass,
  Briefcase,
  Coins,
  Heart,
  HeartHandshake,
  Activity,
  Shield,
  Gem,
  Sun,
  Moon,
  Clock,
  Flame,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  MessageSquare,
  ArrowRight,
  Printer,
  ChevronDown,
  ChevronUp,
  Info,
  Calendar,
  Layers,
  Award,
  Hash,
  Palette
} from 'lucide-react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart } from './ChartViewer';
import { ResponsiveChartContainer } from './ResponsiveChartContainer';
import { CurrentPhaseForecastView } from './CurrentPhaseForecastView';
import { calculateNumerology } from '../utils/numerologyEngine';
import { getColorTaxonomy } from './AuspiciousEssentialsBar';
import { getZodiacName, getNakshatraName, getPlanetFullName } from '../utils/language';
import { formatDate } from '../utils/dateUtils';

interface SimpleAnalysisViewProps {
  kundali: CompleteKundali;
  lang: Language;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  onOpenConsultation?: (initialPrompt?: string) => void;
  onPrintReport?: () => void;
}

// Plain-language interpretations for 12 Lagna (Ascendant) signs
const LAGNA_INSIGHTS: Record<string, {
  en: { archetype: string; nature: string; strengths: string[]; advice: string; luckyColor: string; luckyDay: string; deity: string; mantra: string };
  hi: { archetype: string; nature: string; strengths: string[]; advice: string; luckyColor: string; luckyDay: string; deity: string; mantra: string };
}> = {
  Aries: {
    en: {
      archetype: 'The Dynamic Pioneer & Trailblazer',
      nature: 'Energetic, courageous, direct, and action-oriented. You possess natural leadership drive and dislike delays.',
      strengths: ['High initiative & courage', 'Quick decision maker', 'Passionate & straightforward'],
      advice: 'Cultivate patience and avoid impulsive reactions in personal conversations.',
      luckyColor: 'Red, Coral, Warm Amber',
      luckyDay: 'Tuesday (मंगलवार)',
      deity: 'Lord Hanuman & Lord Kartikeya',
      mantra: 'ॐ अं अंगारकाय नमः (Om Angarakaya Namah)'
    },
    hi: {
      archetype: 'साहसी एवं नेतृत्वकारी व्यक्तित्व (मेष लग्न)',
      nature: 'आप ऊर्जावान, निडर, स्पष्टवादी और त्वरित निर्णय लेने वाले स्वभाव के हैं। पहल करने में सदैव आगे रहते हैं।',
      strengths: ['साहस एवं ऊर्जावान स्वभाव', 'तत्काल निर्णय लेने की क्षमता', 'स्पष्टवादिता और नेतृत्व क्षमता'],
      advice: 'जल्दबाजी और क्रोध पर नियंत्रण रखें, दूसरों की बात धैर्यपूर्वक सुनें।',
      luckyColor: 'लाल, नारंगी, मूंगा रंग',
      luckyDay: 'मंगलवार',
      deity: 'श्री हनुमान जी',
      mantra: 'ॐ हं हनुमते नमः'
    }
  },
  Taurus: {
    en: {
      archetype: 'The Grounded Builder & Sustainer',
      nature: 'Calm, patient, aesthetic, and persevering. You value stability, security, quality lifestyle, and enduring loyalties.',
      strengths: ['Reliable & steadfast', 'Appreciation for art & luxury', 'Practical financial sense'],
      advice: 'Embrace constructive changes and avoid resisting new approaches due to comfort zones.',
      luckyColor: 'White, Silver, Soft Pink, Turquoise',
      luckyDay: 'Friday (शुक्रवार)',
      deity: 'Goddess Lakshmi',
      mantra: 'ॐ शुं शुक्राय नमः (Om Shum Shukraya Namah)'
    },
    hi: {
      archetype: 'स्थिर, कलाप्रेमी एवं व्यावहारिक व्यक्तित्व (वृषभ लग्न)',
      nature: 'आप शांत, धैर्यवान, सौंदर्यप्रिय और जीवन में स्थायित्व व वित्तीय सुरक्षा को प्राथमिकता देने वाले हैं।',
      strengths: ['विश्वसनीयता एवं धैर्य', 'उत्कृष्ट वित्तीय समझ', 'सद्भाव और सुरुचिपूर्ण जीवनशैली'],
      advice: 'हठी स्वभाव से बचें और आवश्यकतानुसार नए विचारों को सहजता से स्वीकार करें।',
      luckyColor: 'सफेद, चमकीला गुलाबी, फिरोजी',
      luckyDay: 'शुक्रवार',
      deity: 'माता महालक्ष्मी',
      mantra: 'ॐ श्रीं महालक्ष्म्यै नमः'
    }
  },
  Gemini: {
    en: {
      archetype: 'The Curious Communicator & Thinker',
      nature: 'Intellectual, versatile, sociable, and quick-witted. You thrive on learning, networking, and exchanging fresh ideas.',
      strengths: ['Fluent communication', 'Mental agility & curiosity', 'Adaptable to changes'],
      advice: 'Avoid scattering your focus across too many projects simultaneously; finish what you start.',
      luckyColor: 'Emerald Green, Mint, Light Yellow',
      luckyDay: 'Wednesday (बुधवार)',
      deity: 'Lord Ganesha & Lord Vishnu',
      mantra: 'ॐ बुं बुधाय नमः (Om Bum Budhaya Namah)'
    },
    hi: {
      archetype: 'बुद्धिमान, जिज्ञासु एवं संवादकुशल व्यक्तित्व (मिथुन लग्न)',
      nature: 'आप बहुमुखी प्रतिभा, तीव्र बुद्धि, वाक्पटुता और नई जानकारी सीखने के प्रति अत्यधिक उत्सुक रहने वाले हैं।',
      strengths: ['उत्कृष्ट संवाद व लेखन कला', 'तीक्ष्ण बुद्धि व सीखने की लगन', 'त्वरित अनुकूलनशीलता'],
      advice: 'एक समय में बहुत अधिक कार्यों में उलझने से बचें, कार्यों को पूरा करने पर ध्यान दें।',
      luckyColor: 'हरा, तोतिया, हल्का पीला',
      luckyDay: 'बुधवार',
      deity: 'भगवान श्री गणेश जी',
      mantra: 'ॐ गं गणपतये नमः'
    }
  },
  Cancer: {
    en: {
      archetype: 'The Intuitive Caregiver & Protector',
      nature: 'Empathetic, deeply intuitive, protective, and family-centered. You feel energies deeply and care immensely for loved ones.',
      strengths: ['High emotional intelligence', 'Nurturing & supportive', 'Strong gut intuition'],
      advice: 'Guard against mood fluctuations and do not absorb others emotional stress into yourself.',
      luckyColor: 'Pearl White, Cream, Silver',
      luckyDay: 'Monday (सोमवार)',
      deity: 'Lord Shiva & Goddess Parvati',
      mantra: 'ॐ सों सोमाय नमः (Om Som Somaya Namah)'
    },
    hi: {
      archetype: 'संवेदनशील, सहृदय एवं रक्षक व्यक्तित्व (कर्क लग्न)',
      nature: 'आप अत्यधिक संवेदनशील, परिवार-प्रेमी, अंतर्ज्ञानी और दूसरों की भावनाओं को गहराई से समझने वाले हैं।',
      strengths: ['मजबूत भावनात्मक समझ', 'परिवार और मित्रों के प्रति असीम समर्पण', 'सटीक पूर्वाभास क्षमता'],
      advice: 'अत्यधिक भावुकता और मानसिक तनाव से बचें, नियमित ध्यान व प्राणायाम करें।',
      luckyColor: 'दूधिया सफेद, मोती रंग, चांदी',
      luckyDay: 'सोमवार',
      deity: 'भगवान शिव (महादेव)',
      mantra: 'ॐ नमः शिवाय'
    }
  },
  Leo: {
    en: {
      archetype: 'The Noble Leader & Sovereign',
      nature: 'Generous, confident, charismatic, and dignified. You naturally draw respect and lead organizations with grandeur.',
      strengths: ['Magnetic presence & leadership', 'Magnanimous generosity', 'Self-respect & ambition'],
      advice: 'Remain humble and receptive to constructive criticism from trusted peers.',
      luckyColor: 'Golden Yellow, Saffron, Orange',
      luckyDay: 'Sunday (रविवार)',
      deity: 'Lord Surya (Sun God) & Gayatri Mata',
      mantra: 'ॐ घृणि सूर्याय नमः (Om Ghrini Suryaya Namah)'
    },
    hi: {
      archetype: 'प्रतापी, स्वाभिमानी एवं नेतृत्वकारी व्यक्तित्व (सिंह लग्न)',
      nature: 'आप आत्मविश्वासी, उदार, प्रभावशाली और गरिमामयी व्यक्तित्व के धनी हैं। नेतृत्व करना आपका स्वाभाविक गुण है।',
      strengths: ['प्रभावशाली व्यक्तित्व व ओज', 'उदार हृदय एवं विश्वसनीयता', 'सम्मान एवं उच्च लक्ष्य'],
      advice: 'अहंकार और दूसरों पर अत्यधिक अधिकार जताने से बचें, सभी का परामर्श सुनें।',
      luckyColor: 'सुनहरा, केसरिया, गहरा पीला',
      luckyDay: 'रविवार',
      deity: 'भगवान सूर्य देव',
      mantra: 'ॐ सूर्याय नमः'
    }
  },
  Virgo: {
    en: {
      archetype: 'The Analytical Healer & Optimizer',
      nature: 'Methodical, detail-oriented, discerning, and dedicated to service. You excel at problem-solving and organizing chaos.',
      strengths: ['Sharp analytical precision', 'Organized & hardworking', 'Practical problem solving'],
      advice: 'Avoid over-analyzing minor flaws or being overly critical of yourself and others.',
      luckyColor: 'Leaf Green, Beige, Light Turquoise',
      luckyDay: 'Wednesday (बुधवार)',
      deity: 'Lord Vishnu & Ganesha',
      mantra: 'ॐ बुं बुधाय नमः (Om Bum Budhaya Namah)'
    },
    hi: {
      archetype: 'विश्लेषक, कर्तव्यनिष्ठ एवं विवेकशील व्यक्तित्व (कन्या लग्न)',
      nature: 'आप सूक्ष्मदृष्टि रखने वाले, सुव्यवस्थित, सेवाभावी और हर कार्य को अत्यंत बारीकी व निपुणता से करने वाले हैं।',
      strengths: ['बारीक विश्लेषण व तार्किक सोच', 'अनुशासन एवं कर्तव्यपरायणता', 'समस्या समाधान में दक्षता'],
      advice: 'छोटी-छोटी बातों की अत्यधिक चिंता और खुद पर अधिक आलोचना से बचें।',
      luckyColor: 'पन्ना हरा, हल्का हरा, बादामी',
      luckyDay: 'बुधवार',
      deity: 'भगवान विष्णु',
      mantra: 'ॐ नमो भगवते वासुदेवाय'
    }
  },
  Libra: {
    en: {
      archetype: 'The Harmonizer & Diplomat',
      nature: 'Charming, diplomatic, fair-minded, and aesthetic. You seek harmony, graceful partnerships, and justice in all affairs.',
      strengths: ['Diplomacy & peacemaking', 'Refined aesthetic taste', 'Balanced perspective'],
      advice: 'Overcome indecisiveness by trusting your inner core values rather than only seeking external balance.',
      luckyColor: 'Sky Blue, Pastel Pink, Cream, Diamond White',
      luckyDay: 'Friday (शुक्रवार)',
      deity: 'Goddess Saraswati & Lakshmi',
      mantra: 'ॐ शुं शुक्राय नमः (Om Shum Shukraya Namah)'
    },
    hi: {
      archetype: 'सद्भावप्रिय, न्यायप्रिय एवं कूटनीतिज्ञ व्यक्तित्व (तुला लग्न)',
      nature: 'आप आकर्षक, न्यायप्रिय, संतुलित सोच वाले और सुरुचिपूर्ण वातावरण व सुंदर संबंधों को चाहने वाले हैं।',
      strengths: ['कूटनीतिक एवं शांतिप्रिय व्यवहार', 'कला एवं सुरुचि की उत्तम समझ', 'निष्पक्ष निर्णय क्षमता'],
      advice: 'अनिर्णय की स्थिति से बचें, समय पर दृढ़ता से निर्णय लेना सीखें।',
      luckyColor: 'आसमानी, हल्का गुलाबी, क्रीम',
      luckyDay: 'शुक्रवार',
      deity: 'माता लक्ष्मी व सरस्वती',
      mantra: 'ॐ श्रीं नमः'
    }
  },
  Scorpio: {
    en: {
      archetype: 'The Resilient Alchemist & Transformer',
      nature: 'Intense, deeply perceptive, secretive, and resilient. You have immense willpower and rebound stronger from any challenge.',
      strengths: ['Unshakable willpower', 'Deep psychological perception', 'Fierce loyalty & courage'],
      advice: 'Practice forgiveness and release past grievances to unlock your highest spiritual power.',
      luckyColor: 'Deep Maroon, Rust, Blood Red',
      luckyDay: 'Tuesday (मंगलवार)',
      deity: 'Lord Shiva (Bhairava) & Hanuman',
      mantra: 'ॐ अं अंगारकाय नमः (Om Angarakaya Namah)'
    },
    hi: {
      archetype: 'गूढ़, दृढ़ निश्चयी एवं परिवर्तनकारी व्यक्तित्व (वृश्चिक लग्न)',
      nature: 'आप अत्यधिक दृढ़ संकल्प, गहरी समझ, रहस्यमयी और किसी भी परिस्थिति से जीतकर पुनः उभरने की शक्ति रखते हैं।',
      strengths: ['अडिग इच्छाशक्ति व साहस', 'गहन अंतर्दृष्टि व विश्लेषणात्मक शक्ति', 'सच्ची निष्ठा व विश्वसनीयता'],
      advice: 'पुरानी बातों और मनमुटाव को मन में बांधकर न रखें, क्षमा का अभ्यास करें।',
      luckyColor: 'गहरा लाल, मैरून, सिंदूरी',
      luckyDay: 'मंगलवार',
      deity: 'श्री हनुमान जी एवं काल भैरव',
      mantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः'
    }
  },
  Sagittarius: {
    en: {
      archetype: 'The Philosophical Seeker & Visionary',
      nature: 'Optimistic, philosophical, righteous, and visionary. You love truth, higher learning, adventure, and expansive horizons.',
      strengths: ['Natural optimism & wisdom', 'Honesty & ethical principles', 'Expansive visionary outlook'],
      advice: 'Exercise tact in communication; raw bluntness can sometimes inadvertently hurt sensitive feelings.',
      luckyColor: 'Saffron, Bright Gold, Yellow',
      luckyDay: 'Thursday (गुरुवार)',
      deity: 'Lord Dakshinamurthy & Brihaspati',
      mantra: 'ॐ बृं बृहस्पतये नमः (Om Brim Brihaspataye Namah)'
    },
    hi: {
      archetype: 'ज्ञानपिपासु, धर्मपरायण एवं दूरदर्शी व्यक्तित्व (धनु लग्न)',
      nature: 'आप आशावादी, दार्शनिक, सत्यवादी और उच्च ज्ञान व आध्यात्मिक लक्ष्यों की ओर अग्रसर रहने वाले हैं।',
      strengths: ['सकारात्मक सोच एवं स्वाभाविक ज्ञान', 'नैतिकता एवं न्यायप्रियता', 'दूरगामी सोच एवं उत्साह'],
      advice: 'अत्यधिक स्पष्टवादिता में कोमलता का ध्यान रखें ताकि दूसरों की भावनाएं आहत न हों।',
      luckyColor: 'पीला, केसरिया, सुनहरा',
      luckyDay: 'गुरुवार',
      deity: 'भगवान श्री हरि विष्णु एवं देवगुरु बृहस्पति',
      mantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः'
    }
  },
  Capricorn: {
    en: {
      archetype: 'The Master Strategist & Architect',
      nature: 'Disciplined, pragmatic, ambitious, and enduring. You build enduring success through patience, structure, and hard work.',
      strengths: ['Unmatched persistence & discipline', 'Pragmatic long-term planning', 'Integrity & accountability'],
      advice: 'Take time to celebrate milestones and allow warmth into your daily routines rather than just all work.',
      luckyColor: 'Navy Blue, Charcoal, Deep Brown',
      luckyDay: 'Saturday (शनिवार)',
      deity: 'Lord Shiva & Lord Shani',
      mantra: 'ॐ शं शनैश्चराय नमः (Om Sham Shanaishcharaya Namah)'
    },
    hi: {
      archetype: 'अनुशासित, कर्मठ एवं रणनीतिकार व्यक्तित्व (मकर लग्न)',
      nature: 'आप अत्यंत परिश्रमी, व्यावहारिक, धैर्यवान और निरंतर प्रयास से अपने लक्ष्यों को प्राप्त करने वाले हैं।',
      strengths: ['कठिन परिश्रम एवं अनुशासन', 'दीर्घकालिक योजना एवं स्थायित्व', 'जिम्मेदारी व विश्वसनीयता'],
      advice: 'अत्यधिक कार्यभार से मानसिक तनाव न लें, परिवार व विश्राम को भी समय दें।',
      luckyColor: 'नीला, गहरा स्लेटी, काला',
      luckyDay: 'शनिवार',
      deity: 'भगवान शनिदेव एवं शिव जी',
      mantra: 'ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः'
    }
  },
  Aquarius: {
    en: {
      archetype: 'The Humanitarian Innovator & Free Spirit',
      nature: 'Forward-thinking, humanitarian, original, and independent. You think ahead of your time and value progressive concepts.',
      strengths: ['Visionary innovative thinking', 'Egalitarian & socially minded', 'Unconventional creativity'],
      advice: 'Connect emotionally with close individuals rather than viewing relationships purely conceptually.',
      luckyColor: 'Electric Blue, Violet, Silver',
      luckyDay: 'Saturday (शनिवार)',
      deity: 'Lord Shiva & Hanuman',
      mantra: 'ॐ शं शनैश्चराय नमः (Om Sham Shanaishcharaya Namah)'
    },
    hi: {
      archetype: 'दूरदर्शी, नवप्रवर्तनकारी एवं समाजहितैषी व्यक्तित्व (कुंभ लग्न)',
      nature: 'आप प्रगतिशील, मौलिक सोच वाले, स्वतंत्र विचारों के धनी और व्यापक समाज के कल्याण की सोचने वाले हैं।',
      strengths: ['अद्भुत नई सोच एवं रचनात्मकता', 'समानता व निष्पक्षता का दृष्टिकोण', 'अनुसंधान एवं तकनीकी समझ'],
      advice: 'अपनों के साथ भावनात्मक रूप से जुड़ें और मन की बातें साझा करें।',
      luckyColor: 'आसमानी नीला, बैंगनी, सलेटी',
      luckyDay: 'शनिवार',
      deity: 'भगवान शिव एवं श्री हनुमान जी',
      mantra: 'ॐ शं शनये नमः'
    }
  },
  Pisces: {
    en: {
      archetype: 'The Compassionate Mystic & Visionary',
      nature: 'Imaginative, compassionate, spiritual, and artistic. You possess profound empathy and deep philosophical intuition.',
      strengths: ['Deep empathy & kindness', 'Vivid artistic imagination', 'Spiritual wisdom & intuition'],
      advice: 'Maintain clear practical boundaries and ground your creative visions in structured daily habits.',
      luckyColor: 'Turquoise, Light Yellow, Pearl White',
      luckyDay: 'Thursday (गुरुवार)',
      deity: 'Lord Vishnu & Shiva',
      mantra: 'ॐ बृं बृहस्पतये नमः (Om Brim Brihaspataye Namah)'
    },
    hi: {
      archetype: 'करुणामयी, आध्यात्मिक एवं कलात्मक व्यक्तित्व (मीन लग्न)',
      nature: 'आप अत्यंत दयालु, कल्पनाशील, अंतर्दृष्टि सम्पन्न और आध्यात्मिक चेतना से ओत-प्रोत व्यक्तित्व के हैं।',
      strengths: ['गहरी संवेदनशीलता एवं परोपकार', 'सृजनात्मक कल्पना एवं कलात्मक रुचि', 'आध्यात्मिक रुझान'],
      advice: 'व्यावहारिक सीमाओं का ध्यान रखें और अपने विचारों को धरातल पर क्रियान्वित करें।',
      luckyColor: 'हल्का पीला, सुनहरी केसरिया, समुद्री हरा',
      luckyDay: 'गुरुवार',
      deity: 'भगवान विष्णु (नारायण)',
      mantra: 'ॐ नमो नारायणाय'
    }
  }
};

export const SimpleAnalysisView: React.FC<SimpleAnalysisViewProps> = ({
  kundali,
  lang,
  chartStyle,
  setChartStyle,
  onOpenConsultation,
  onPrintReport
}) => {
  const isHi = lang === 'hi';
  const { birthDetails, houses, planets, panchang, gemstones, dashas, sadeSati } = kundali;
  
  const lagnaHouse = houses[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Taurus';
  const sunPlanet = planets.find((p) => p.name === 'Sun');
  const jupiterPlanet = planets.find((p) => p.name === 'Jupiter');
  const saturnPlanet = planets.find((p) => p.name === 'Saturn');

  const lagnaData = LAGNA_INSIGHTS[lagnaSign] || LAGNA_INSIGHTS.Aries;
  const content = isHi ? lagnaData.hi : lagnaData.en;

  // Active running Dasha
  const currentMD = dashas.find((d) => d.isCurrent);
  const currentAD = currentMD?.antardashas?.find((ad) => ad.isCurrent);

  // Selected interactive house on simplified chart
  const [selectedHouse, setSelectedHouse] = useState<number>(1);
  const [activeAreaTab, setActiveAreaTab] = useState<'career' | 'wealth' | 'love' | 'health' | 'timing'>('career');

  // Primary Life Stone
  const lifeGem = gemstones?.find((g) => g.type === 'Life Stone (Lagna)') || gemstones?.[0] || {
    gemstone: 'Yellow Sapphire / Pukhraj',
    substitute: 'Yellow Topaz',
    finger: 'Index Finger',
    metal: 'Gold / Brass',
    day: 'Thursday morning'
  };

  // Numerology Analysis (Driver & Destiny Numbers)
  const numerology = React.useMemo(() => {
    return calculateNumerology(
      kundali.birthDetails?.date || '1990-01-01',
      kundali.birthDetails?.name || 'Jataka',
      kundali.birthDetails?.gender || 'male'
    );
  }, [kundali.birthDetails]);

  // Color Taxonomy (Lucky, Neutral & Avoid Colors)
  const colorTaxonomy = React.useMemo(() => {
    return getColorTaxonomy(lagnaSign, numerology.moolank, lang, content.luckyColor);
  }, [lagnaSign, numerology.moolank, lang, content.luckyColor]);

  // Helper for house planets
  const getHousePlanets = (hNum: number) => {
    return planets.filter((p) => p.house === hNum);
  };

  // Plain meanings of 12 Houses
  const HOUSE_PLAIN_MEANINGS: Record<number, { en: { title: string; desc: string; focus: string }; hi: { title: string; desc: string; focus: string } }> = {
    1: {
      en: { title: '1st House (Lagna)', desc: 'Represents your core personality, vitality, physical health, and self-expression.', focus: 'Self, Health, Temperament' },
      hi: { title: 'प्रथम भाव (लग्न)', desc: 'आपके व्यक्तित्व, शारीरिक स्वास्थ्य, आत्मबल और जीवन दृष्टिकोण का मुख्य आधार।', focus: 'व्यक्तित्व, स्वास्थ्य, आत्मबल' }
    },
    2: {
      en: { title: '2nd House (Wealth & Speech)', desc: 'Represents accumulated savings, speech clarity, family values, and eating habits.', focus: 'Savings, Family, Voice' },
      hi: { title: 'द्वितीय भाव (धन व वाणी)', desc: 'संचित धन, बैंक बैलेंस, वाणी का प्रभाव और पारिवारिक संस्कार का भाव।', focus: 'संचित धन, परिवार, वाणी' }
    },
    3: {
      en: { title: '3rd House (Courage & Skills)', desc: 'Represents personal courage, initiatives, younger siblings, and creative communications.', focus: 'Courage, Skills, Siblings' },
      hi: { title: 'तृतीय भाव (पराक्रम व कौशल)', desc: 'साहस, उद्यम, छोटे भाई-बहन, लेखन व डिजिटल संचार कौशल का भाव।', focus: 'साहस, छोटे भाई-बहन, कला' }
    },
    4: {
      en: { title: '4th House (Home & Happiness)', desc: 'Represents mother, home peace, vehicles, properties, and emotional contentment.', focus: 'Home, Mother, Vehicles' },
      hi: { title: 'चतुर्थ भाव (सुख व माता)', desc: 'माता का स्नेह, गृह सुख, वाहन, भूमि-भवन और मन की आंतरिक शांति।', focus: 'गृह सुख, माता, भूमि-वाहन' }
    },
    5: {
      en: { title: '5th House (Wisdom & Children)', desc: 'Represents intellect, past life merits, higher education, creativity, and children.', focus: 'Intellect, Children, Creativity' },
      hi: { title: 'पंचम भाव (विद्या व संतान)', desc: 'बुद्धि, पूर्वजन्म के पुण्य, उच्च शिक्षा, रचनात्मकता और संतान सुख।', focus: 'बुद्धि, संतान, पूर्व पुण्य' }
    },
    6: {
      en: { title: '6th House (Daily Work & Health)', desc: 'Represents overcoming obstacles, daily job routine, competitive drive, and wellness resilience.', focus: 'Overcoming Obstacles, Work' },
      hi: { title: 'षष्ठ भाव (शत्रु, रोग व सेवा)', desc: 'प्रतियोगिता में विजय, दैनिक नौकरी, ऋण प्रबंधन और स्वास्थ्य प्रतिरोधक क्षमता।', focus: 'नौकरी, रोग प्रतिरोध, विजय' }
    },
    7: {
      en: { title: '7th House (Marriage & Partnership)', desc: 'Represents spouse qualities, marital harmony, business partnerships, and public dealings.', focus: 'Spouse, Marriage, Business' },
      hi: { title: 'सप्तम भाव (विवाह व साझेदारी)', desc: 'जीवनसाथी, वैवाहिक सामंजस्य, व्यापारिक साझेदारी और सामाजिक संबंध।', focus: 'जीवनसाथी, वैवाहिक सुख, व्यापार' }
    },
    8: {
      en: { title: '8th House (Transformation & Longevity)', desc: 'Represents deep research, longevity, occult wisdom, and sudden regenerative shifts.', focus: 'Transformation, Longevity' },
      hi: { title: 'अष्टम भाव (आयु व अनुसंधान)', desc: 'आयु, गुप्त ज्ञान, शोध, पैतृक संपत्ति और जीवन के गहन परिवर्तन।', focus: 'आयु, गुप्त विद्या, रूपांतरण' }
    },
    9: {
      en: { title: '9th House (Fortune & Dharma)', desc: 'Represents divine luck (Bhagya), spirituality, father, pilgrimages, and higher ethics.', focus: 'Fortune (Bhagya), Dharma, Father' },
      hi: { title: 'नवम भाव (भाग्य व धर्म)', desc: 'ईश्वरीय कृपा, भाग्य, धर्म, पिता का सहयोग और लंबी शुभ यात्राएं।', focus: 'भाग्य, धर्म, पिता का आशीर्वाद' }
    },
    10: {
      en: { title: '10th House (Career & Fame)', desc: 'Represents professional success, leadership, public fame, and societal achievements.', focus: 'Career, Fame, Leadership' },
      hi: { title: 'दशम भाव (कर्म व मान-सम्मान)', desc: 'कार्यक्षेत्र, पद-प्रतिष्ठा, प्रशासनिक सफलता और समाज में मान-सम्मान।', focus: 'करियर, प्रतिष्ठा, प्रशासनिक सफलता' }
    },
    11: {
      en: { title: '11th House (Gains & Network)', desc: 'Represents regular income, fulfillment of aspirations, friendships, and profitable expansion.', focus: 'Gains, Income, Network' },
      hi: { title: 'एकादश भाव (आय व लाभ)', desc: 'नियमित आर्थिक लाभ, मनोकामनाओं की पूर्ति, बड़े भाई-बहन और मित्र मंडल।', focus: 'आर्थिक लाभ, मनोकामना पूर्ति' }
    },
    12: {
      en: { title: '12th House (Spiritual Peace & Foreign)', desc: 'Represents foreign connections, expenditure balance, meditation, and spiritual liberation.', focus: 'Foreign, Meditation, Peace' },
      hi: { title: 'द्वादश भाव (मोक्ष व विदेश)', desc: 'विदेश यात्रा, व्यय संतुलन, ध्यान-साधना और आध्यात्मिक शांति।', focus: 'विदेश संबंध, साधना, मोक्ष' }
    }
  };

  const selectedHouseInfo = isHi ? HOUSE_PLAIN_MEANINGS[selectedHouse].hi : HOUSE_PLAIN_MEANINGS[selectedHouse].en;
  const selectedHouseObj = houses.find((h) => h.houseNumber === selectedHouse);
  const selectedHouseSign = selectedHouseObj?.sign || '';
  const selectedHouseLord = selectedHouseObj?.signLord || '';
  const selectedHousePlanets = getHousePlanets(selectedHouse);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. HERO PERSONALITY & AUSPICIOUS OVERVIEW CARD */}
      <div className="bg-gradient-to-br from-astro-bg via-[#FAF6ED] to-[#F3EDE2] border border-astro-border rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5 border-b border-astro-border/80 pb-5">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full">
                {isHi ? 'सरल जन्म कुंडली फलकथन' : 'Plain-Language Life Reading'}
              </span>
              <span className="text-xs text-astro-accent font-semibold">
                {isHi ? 'लग्न:' : 'Lagna:'} <strong className="text-astro-primary">{getZodiacName(lagnaSign, lang)}</strong> • {isHi ? 'चंद्र राशि:' : 'Moon:'} <strong className="text-astro-primary">{getZodiacName(moonSign, lang)}</strong>
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-astro-text tracking-tight">
              {content.archetype}
            </h2>
            <p className="text-sm text-astro-secondary-text leading-relaxed max-w-3xl">
              {content.nature}
            </p>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
            {onOpenConsultation && (
              <button
                onClick={() => onOpenConsultation(isHi ? `कृपया मेरी कुंडली (${lagnaSign} लग्न, ${moonSign} राशि) के आधार पर मेरे करियर और भविष्य का सरल मार्गदर्शन दें।` : `Please give me a clear, plain-language guidance on my career and life path based on my ${lagnaSign} Lagna.`)}
                className="flex-1 sm:flex-none px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all hover:scale-[1.02]"
              >
                <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                <span>{isHi ? 'एआई ज्योतिषी से पूछें' : 'Ask AI Astrologer'}</span>
              </button>
            )}
            {onPrintReport && (
              <button
                onClick={onPrintReport}
                className="px-3.5 py-2 rounded-xl bg-astro-card hover:bg-astro-bg text-astro-text border border-astro-border text-xs font-semibold flex items-center justify-center gap-1.5 shadow-xs transition-all"
                title={isHi ? 'रिपोर्ट प्रिंट करें' : 'Print Report'}
              >
                <Printer className="w-3.5 h-3.5 text-astro-primary" />
                <span className="hidden sm:inline">{isHi ? 'प्रिंट' : 'Print'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Auspicious Keys Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-5">
          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-astro-accent flex items-center gap-1">
              <Gem className="w-3 h-3 text-astro-accent" />
              {isHi ? 'भाग्य रत्न' : 'Lucky Gemstone'}
            </div>
            <div className="text-xs font-bold text-astro-primary truncate">{lifeGem.gemstone}</div>
            <div className="text-[10px] text-astro-secondary-text">{lifeGem.metal || 'Gold / Brass'}</div>
          </div>

          {/* Lucky Numbers (शुभ अंक) */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'))}
            className="bg-astro-bg hover:bg-amber-500/10 p-3 rounded-xl border border-amber-300/80 hover:border-amber-500 shadow-2xs space-y-1 text-left transition-all cursor-pointer group"
            title={isHi ? 'विस्तृत शुभ अंक व रंग देखें' : 'View Lucky Numbers & Colors Guide'}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'शुभ अंक' : 'Lucky Numbers'}
              </span>
              <span className="text-[9px] text-amber-600 font-semibold underline opacity-0 group-hover:opacity-100 transition-opacity">
                {isHi ? 'देखें' : 'View'}
              </span>
            </div>
            <div className="text-xs font-bold text-amber-800 dark:text-amber-300 truncate">
              {isHi ? `मूलांक ${numerology.moolank} / भाग्यांक ${numerology.bhagyank}` : `Driver ${numerology.moolank} / Path ${numerology.bhagyank}`}
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate">
              {isHi ? `मित्र: ${numerology.luckyNumbers.join(', ')}` : `Friendly: ${numerology.luckyNumbers.join(', ')}`}
            </div>
          </button>

          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'))}
            className="bg-astro-bg hover:bg-rose-500/10 p-3 rounded-xl border border-astro-border hover:border-rose-400 shadow-2xs space-y-1 text-left transition-all cursor-pointer group"
            title={isHi ? 'विस्तृत शुभ, तटस्थ व वर्जित रंग विवरण देखें' : 'View Lucky, Neutral & Avoid Colors Guide'}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Palette className="w-3 h-3 text-rose-600 dark:text-rose-400 group-hover:scale-110 transition-transform" />
                {isHi ? 'रंग मार्गदर्शन' : 'Color Harmony'}
              </span>
              <span className="text-[9px] text-rose-600 font-semibold underline opacity-0 group-hover:opacity-100 transition-opacity">
                {isHi ? 'देखें' : 'View'}
              </span>
            </div>
            <div className="text-xs font-bold text-astro-text truncate">
              <span className="text-emerald-700 dark:text-emerald-400 font-bold">{isHi ? 'शुभ: ' : 'Lucky: '}</span>
              {colorTaxonomy.lucky.summary}
            </div>
            <div className="text-[10px] text-astro-secondary-text truncate">
              <span className="text-rose-700 dark:text-rose-400 font-semibold">{isHi ? 'वर्जित: ' : 'Avoid: '}</span>
              {colorTaxonomy.avoid.summary}
            </div>
          </button>

          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
              <Calendar className="w-3 h-3 text-astro-primary" />
              {isHi ? 'शुभ दिन' : 'Favorable Day'}
            </div>
            <div className="text-xs font-bold text-astro-primary">{content.luckyDay}</div>
            <div className="text-[10px] text-astro-secondary-text">{isHi ? 'महत्वपूर्ण कार्यों हेतु' : 'For key milestones'}</div>
          </div>

          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-astro-accent flex items-center gap-1">
              <Compass className="w-3 h-3 text-astro-accent" />
              {isHi ? 'शुभ दिशा' : 'Lucky Direction'}
            </div>
            <div className="text-xs font-bold text-astro-text">
              {lagnaSign === 'Aries' || lagnaSign === 'Leo' || lagnaSign === 'Sagittarius' ? (isHi ? 'पूर्व (East)' : 'East') :
               lagnaSign === 'Taurus' || lagnaSign === 'Virgo' || lagnaSign === 'Capricorn' ? (isHi ? 'दक्षिण (South)' : 'South') :
               lagnaSign === 'Gemini' || lagnaSign === 'Libra' || lagnaSign === 'Aquarius' ? (isHi ? 'पश्चिम (West)' : 'West') :
               (isHi ? 'उत्तर (North)' : 'North')}
            </div>
            <div className="text-[10px] text-astro-secondary-text">{isHi ? 'कार्य / अध्ययन हेतु' : 'Work & Study'}</div>
          </div>

          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
              <Moon className="w-3 h-3 text-astro-primary" />
              {isHi ? 'इष्ट देव / देवी' : 'Ruling Deva'}
            </div>
            <div className="text-xs font-bold text-astro-text truncate">{content.deity.split('&')[0]}</div>
            <div className="text-[10px] text-astro-secondary-text">{isHi ? 'आशीर्वाद एवं शांति' : 'Peace & Guidance'}</div>
          </div>

          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1">
              <Clock className="w-3 h-3 text-astro-primary" />
              {isHi ? 'वर्तमान समय' : 'Running Dasha'}
            </div>
            <div className="text-xs font-bold text-astro-accent truncate">
              {currentMD?.planet} - {currentAD?.planet}
            </div>
            <div className="text-[10px] text-astro-secondary-text">
              {currentAD?.endDate ? formatDate(currentAD.endDate) : (isHi ? 'सक्रिय' : 'Active')}
            </div>
          </div>
        </div>
      </div>

      {/* 2. LIFE AREAS DEEP-DIVE SECTION */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3 border-b border-astro-border pb-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold font-serif text-astro-text">
              {isHi ? 'प्रमुख जीवन क्षेत्र विश्लेषण' : 'Life Areas & Everyday Guidance'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHi ? 'करियर, धन, वैवाहिक जीवन और स्वास्थ्य का सरल फलकथन' : 'Clear plain-language insights for career, wealth, relationships and health'}
            </p>
          </div>

          {/* Area Switcher Tabs */}
          <div className="flex bg-astro-card p-1 rounded-xl border border-astro-border text-xs font-semibold overflow-x-auto no-scrollbar">
            {[
              { id: 'career', label: isHi ? 'करियर व आजीविका' : 'Career & Success', icon: Briefcase },
              { id: 'wealth', label: isHi ? 'धन व संपत्ति' : 'Wealth & Finance', icon: Coins },
              { id: 'love', label: isHi ? 'विवाह व संबंध' : 'Love & Marriage', icon: HeartHandshake },
              { id: 'health', label: isHi ? 'स्वास्थ्य व ऊर्जा' : 'Health & Vitality', icon: Activity },
              { id: 'timing', label: isHi ? 'वर्तमान समय (दशा)' : 'Current Time Phase', icon: Clock }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeAreaTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveAreaTab(tab.id as any)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-astro-primary text-white font-bold shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* TAB 1: CAREER */}
        {activeAreaTab === 'career' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                  <Briefcase className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'करियर दिशा एवं उपयुक्त क्षेत्र' : 'Career Direction & Best Environments'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? `आपके लग्न (${getZodiacName(lagnaSign, 'hi')}) और दशम भाव की स्थिति के अनुसार, आप ${
                        lagnaSign === 'Aries' || lagnaSign === 'Leo' || lagnaSign === 'Sagittarius'
                          ? 'प्रशासन, नेतृत्व, प्रबंधन, उद्यमशीलता, रक्षा या उच्च तकनीकी क्षेत्रों में अत्यंत सफल रहेंगे।'
                          : lagnaSign === 'Taurus' || lagnaSign === 'Virgo' || lagnaSign === 'Capricorn'
                          ? 'वित्तीय प्रबंधन, रियल एस्टेट, इंजीनियरिंग, कॉर्पोरेट योजना, बैंकिंग या संगठित व्यापार में दीर्घकालिक सफलता प्राप्त करेंगे।'
                          : lagnaSign === 'Gemini' || lagnaSign === 'Libra' || lagnaSign === 'Aquarius'
                          ? 'डिजिटल मीडिया, संचार, व्यापारिक सौदेबाजी, परामर्श, सूचना प्रौद्योगिकी और जनसंपर्क में उत्कृष्ट प्रदर्शन करेंगे।'
                          : 'परामर्श, चिकित्सा/स्वास्थ्य सेवा, अनुसंधान, शिक्षा, रचनात्मक कला अथवा जनकल्याणकारी कार्यों में स्वाभाविक प्रतिष्ठा पाएंगे।'
                      }`
                    : `Based on your ${lagnaSign} Lagna and 10th House alignment, you are naturally suited for ${
                        lagnaSign === 'Aries' || lagnaSign === 'Leo' || lagnaSign === 'Sagittarius'
                          ? 'leadership, executive management, entrepreneurship, technology leadership, and independent decision-making roles.'
                          : lagnaSign === 'Taurus' || lagnaSign === 'Virgo' || lagnaSign === 'Capricorn'
                          ? 'financial management, strategic corporate planning, engineering, structured business operations, and tangible asset growth.'
                          : lagnaSign === 'Gemini' || lagnaSign === 'Libra' || lagnaSign === 'Aquarius'
                          ? 'communications, digital strategy, consultative advisory, negotiation, software/IT, and international commerce.'
                          : 'advisory, healthcare, academic research, creative industries, psychology, and public-service institutions.'
                      }`}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'नौकरी बनाम स्वतंत्र व्यवसाय' : 'Job vs Business Suitability'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {saturnPlanet?.house === 10 || saturnPlanet?.house === 6
                      ? (isHi ? 'संगठित कॉर्पोरेट या सरकारी सेवा में श्रेष्ठ लाभ' : 'High stability in Structured Corporate / Government Service')
                      : (isHi ? 'स्वतंत्र व्यवसाय व परामर्श में उच्च विस्तार' : 'Strong expansion in Independent Business & Advisory')}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'अनुभव व कार्यकुशलता के साथ उन्नति निरंतर बढ़ेगी।' : 'Career maturity accelerates consistently after your 28th-30th year.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'सफलता का मूल मंत्र' : 'Key Golden Rule'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'धैर्यपूर्वक विशेषज्ञता का विकास' : 'Mastery through Continuous Focus'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'जल्दबाजी के निर्णयों से बचें और अपने कौशल को निरंतर निखारें।' : 'Deepen your specialized domain mastery rather than frequent career pivoting.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt trigger */}
            <div className="p-4 rounded-xl bg-astro-primary/5 border border-astro-primary/20 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-astro-primary font-bold text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'करियर प्रश्न पूछें' : 'Consult on Career'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-1.5 leading-relaxed">
                  {isHi ? 'अपने करियर में पदोन्नति, कार्यक्षेत्र परिवर्तन या व्यवसाय विस्तार हेतु एआई ज्योतिषी से तुरंत परामर्श लें।' : 'Ask our Vedic Jyotish AI specific questions regarding job promotions, business starts, or career timing.'}
                </p>
              </div>

              {onOpenConsultation && (
                <div className="space-y-2">
                  <button
                    onClick={() => onOpenConsultation(isHi ? 'मेरी जन्म कुंडली के अनुसार मेरे करियर में आने वाले 1 वर्ष में क्या मुख्य अवसर, नौकरी परिवर्तन व चुनौतियां रहेंगी?' : 'According to my Kundali, what are the primary career opportunities, job changes, and milestones for me over the next 1 year?')}
                    className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-between gap-1.5 shadow-2xs transition-all"
                  >
                    <span>{isHi ? 'आने वाले 1 वर्ष का करियर फल' : 'Ask 1-Year Career Forecast'}</span>
                    <ArrowRight className="w-3 h-3 text-astro-accent" />
                  </button>
                  <button
                    onClick={() => onOpenConsultation(isHi ? 'मेरी जन्म कुंडली के अनुसार मेरे करियर में आने वाले 3 वर्षों में पदोन्नति और व्यावसायिक विस्तार का विस्तृत फल क्या है?' : 'According to my Kundali, what is the comprehensive 3-year career growth, promotion, and professional milestone forecast?')}
                    className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-between gap-1.5 shadow-2xs transition-all"
                  >
                    <span>{isHi ? 'आने वाले 3 वर्ष का करियर फल' : 'Ask 3-Year Career Forecast'}</span>
                    <ArrowRight className="w-3 h-3 text-astro-accent" />
                  </button>
                  <button
                    onClick={() => onOpenConsultation(isHi ? 'मेरी जन्म कुंडली के अनुसार आने वाले 5 वर्षों में दीर्घकालिक करियर, नेतृत्व और व्यावसायिक सफलता का पूर्वानुमान क्या है?' : 'According to my Kundali, what is the long-term 5-year strategic career, leadership, and enterprise expansion forecast?')}
                    className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-between gap-1.5 shadow-2xs transition-all"
                  >
                    <span>{isHi ? 'आने वाले 5 वर्ष का करियर फल' : 'Ask 5-Year Career Forecast'}</span>
                    <ArrowRight className="w-3 h-3 text-astro-accent" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: WEALTH */}
        {activeAreaTab === 'wealth' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                  <Coins className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'धन संचय व आर्थिक स्थिति' : 'Wealth Accumulation & Asset Prosperity'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'आपकी कुंडली में द्वितीय (धन) और एकादश (लाभ) भाव का योग दर्शाता है कि आपके पास नियमित आय प्रवाह बना रहेगा। संपत्ति निर्माण के लिए व्यवस्थित बचत और सुरक्षित निवेश (रियल एस्टेट व दीर्घकालिक फंड) अत्यंत फलदायी रहेंगे।'
                    : 'Your 2nd and 11th house financial alignments indicate steady revenue streams with strong upside during favorable dasha periods. Wealth compounds most effectively through structured long-term asset creation and real estate stability.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'भूमि एवं वाहन सुख' : 'Property & Vehicle Prosperity'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'शुभ व अनुकूल योग' : 'Favorable 4th House Foundations'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'स्वयं के आवास और आरामदायक वाहन का उत्तम सुख प्राप्त होगा।' : 'Strong indications for owning personal residential property and comfortable conveyances.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-secondary bg-astro-secondary/15 px-2 py-0.5 rounded-md">
                    {isHi ? 'सावधानी' : 'Financial Caution'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'अति-जोखिम वाले सट्टेबाजी से बचाव' : 'Avoid Unverified Speculation'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'त्वरित लाभ के प्रलोभन से बचें, ठोस निवेश को प्राथमिकता दें।' : 'Prefer disciplined, research-backed investments over unverified speculative bets.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt trigger */}
            <div className="p-4 rounded-xl bg-astro-primary/5 border border-astro-primary/20 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-astro-primary font-bold text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'आर्थिक परामर्श' : 'Wealth Advisory'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-1.5 leading-relaxed">
                  {isHi ? 'धन वृद्धि, निवेश का शुभ समय अथवा पैतृक संपत्ति के प्रश्नों पर तुरंत विचार विमर्श करें।' : 'Consult on wealth timing, auspicious purchase windows, and financial growth.'}
                </p>
              </div>

              {onOpenConsultation && (
                <button
                  onClick={() => onOpenConsultation(isHi ? 'मेरी कुंडली के अनुसार संपत्ति खरीदने और धन वृद्धि के लिए सबसे अनुकूल समय कौन सा है?' : 'What is the most auspicious window for property purchase and financial investments according to my chart?')}
                  className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-2xs transition-all"
                >
                  <span>{isHi ? 'निवेश व संपत्ति का समय पूछें' : 'Ask Property & Wealth Timing'}</span>
                  <ArrowRight className="w-3 h-3 text-astro-accent" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: LOVE & MARRIAGE */}
        {activeAreaTab === 'love' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                  <HeartHandshake className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'जीवनसाथी के गुण एवं वैवाहिक सामंजस्य' : 'Partner Qualities & Marital Harmony'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'आपकी कुंडली का सप्तम भाव दर्शाता है कि आपका जीवनसाथी समझदार, सहयोगी और पारिवारिक मूल्यों को सम्मान देने वाला होगा। आपसी संवाद में पारदर्शिता और सम्मान बनाए रखने से दांपत्य जीवन में सुख व समृद्धि सदैव बढ़ेगी।'
                    : 'Your 7th house and Venus indications reflect a supportive, dignified, and value-oriented partner. Nurturing open communication, mutual respect, and shared family goals ensures enduring relationship harmony.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'जीवनसाथी का स्वभाव' : 'Partner Nature & Temperament'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'बुद्धिमान, व्यावहारिक एवं निष्ठावान' : 'Intelligent, Supportive & Grounded'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'जीवन के कठिन समय में आपका संबल बनेंगे।' : 'Offers thoughtful advice and stands steadfast in life decisions.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'संबंध मधुरता उपाय' : 'Harmony Practice'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'शुक्रवार को मीठी खीर का भोग व शिव-पार्वती पूजन' : 'Friday Lakshmi-Shiva Harmony Offering'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'आपसी स्नेह व शांति के लिए अत्यंत शुभ।' : 'Fosters mutual affection, softness, and understanding.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt trigger */}
            <div className="p-4 rounded-xl bg-astro-primary/5 border border-astro-primary/20 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-astro-primary font-bold text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'विवाह परामर्श' : 'Marriage Consultation'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-1.5 leading-relaxed">
                  {isHi ? 'विवाह का शुभ समय, गुण मिलान या दांपत्य जीवन के प्रश्नों पर विस्तृत मार्गदर्शन प्राप्त करें।' : 'Ask about marriage timing, partner compatibility, and relationship remedies.'}
                </p>
              </div>

              {onOpenConsultation && (
                <button
                  onClick={() => onOpenConsultation(isHi ? 'मेरी कुंडली के अनुसार विवाह के लिए सबसे शुभ समय व जीवनसाथी का स्वभाव कैसा रहेगा?' : 'When is the most auspicious window for marriage and what are my partner qualities according to my chart?')}
                  className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-2xs transition-all"
                >
                  <span>{isHi ? 'विवाह का समय व योग पूछें' : 'Ask Marriage Timing'}</span>
                  <ArrowRight className="w-3 h-3 text-astro-accent" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: HEALTH */}
        {activeAreaTab === 'health' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                  <Activity className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'स्वास्थ्य संतुलन एवं ऊर्जा रक्षा' : 'Vitality Balance & Wellness Hygiene'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'आपकी शारीरिक ऊर्जा का स्तर उत्तम है। नियमित दिनचर्या, पर्याप्त जलयोजन, समय पर भोजन और मानसिक तनाव से दूरी बनाए रखना आपके ओज और स्वास्थ्य को सदा बनाए रखेगा।'
                    : 'Your baseline vitality is robust. Maintaining a steady daily rhythm, adequate hydration, nutritious home-cooked meals, and periodic mental relaxation prevents metabolic fatigue.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'संवेदनशील क्षेत्र' : 'Areas to Care'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'पाचन क्रिया व मानसिक विश्राम' : 'Digestive Rhythm & Sleep Quality'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'देर रात भोजन और अधिक तनाव से बचें।' : 'Avoid late-night heavy meals; prioritize 7+ hours of restful sleep.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'दैनिक उत्तम आदत' : 'Recommended Habit'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'प्रातः सूर्य दर्शन एवं प्राणायाम' : 'Morning Surya Namaskar & Pranayama'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'शरीर में सकारात्मक ऊर्जा का संचार करता है।' : 'Infuses vitality, focus, and digestive fire.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt trigger */}
            <div className="p-4 rounded-xl bg-astro-primary/5 border border-astro-primary/20 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-astro-primary font-bold text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'स्वास्थ्य उपाय' : 'Health & Vitality'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-1.5 leading-relaxed">
                  {isHi ? 'ग्रहों के अनुसार स्वास्थ्य संतुलन, मानसिक शांति एवं आयुर्वेदिक सावधानियों पर परामर्श लें।' : 'Consult on astrological wellness practices, gemstone vitality, and mental peace.'}
                </p>
              </div>

              {onOpenConsultation && (
                <button
                  onClick={() => onOpenConsultation(isHi ? 'मेरी कुंडली के अनुसार मुझे स्वास्थ्य व मानसिक शांति के लिए कौन से दैनिक उपाय करने चाहिए?' : 'What daily astrological wellness remedies should I practice for optimal vitality and peace of mind?')}
                  className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-2xs transition-all"
                >
                  <span>{isHi ? 'स्वास्थ्य उपाय पूछें' : 'Ask Health Remedies'}</span>
                  <ArrowRight className="w-3 h-3 text-astro-accent" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* TAB 5: CURRENT TIMING */}
        {activeAreaTab === 'timing' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                  <Clock className="w-4 h-4 text-astro-accent" />
                  <span>
                    {isHi ? 'वर्तमान महादशा व अंतर्दशा फल' : 'Current Mahadasha & Antardasha Dynamics'}
                  </span>
                </div>
                <div className="text-sm font-bold text-astro-primary font-serif">
                  {currentMD ? `${getPlanetFullName(currentMD.planet, lang)} महादशा • ${getPlanetFullName(currentAD?.planet || '', lang)} अंतर्दशा` : 'Active Life Cycle'}
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? `आप वर्तमान में ${getPlanetFullName(currentMD?.planet || '', 'hi')} की महादशा और ${getPlanetFullName(currentAD?.planet || '', 'hi')} की अंतर्दशा से गुजर रहे हैं। यह समय आपके ज्ञान, कार्यक्षेत्र और निर्णय क्षमता के विकास के लिए विशेष रूप से सक्रिय है। नए कौशल सीखने और योजनाओं को क्रियान्वित करने के लिए यह अनुकूल अवधि है।`
                    : `You are currently experiencing the ${currentMD?.planet} Mahadasha with ${currentAD?.planet} Antardasha. This period highlights personal learning, career refinement, and strategic planning. A proactive, grounded approach yields fruitful long-term results.`}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md">
                    {isHi ? 'मुख्य अवसर' : 'Key Opportunity'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {isHi ? 'व्यावसायिक विस्तार एवं नए संपर्क' : 'Professional Growth & Strategic Alliances'}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'नई जिम्मेदारियों को स्वीकार करें।' : 'Step up to take new leadership responsibilities.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-astro-bg border border-astro-border space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-md">
                    {isHi ? 'समय सीमा' : 'Active Until'}
                  </span>
                  <div className="text-xs font-bold text-astro-text">
                    {currentAD?.endDate ? formatDate(currentAD.endDate) : (isHi ? 'वर्तमान में सक्रिय' : 'Currently Active')}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'इसके उपरांत नया उप-चक्र आरंभ होगा।' : 'Following this, the next sub-period will activate.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Prompt trigger */}
            <div className="p-4 rounded-xl bg-astro-primary/5 border border-astro-primary/20 flex flex-col justify-between space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-astro-primary font-bold text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'दशा भविष्यफल' : 'Dasha Timing Forecast'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text mt-1.5 leading-relaxed">
                  {isHi ? 'वर्तमान दशा के दौरान आने वाले बड़े बदलावों और शुभ समय की तिथियों के बारे में एआई ज्योतिषी से पूछें।' : 'Ask our AI Astrologer what major turning points will activate during your active planetary period.'}
                </p>
              </div>

              {onOpenConsultation && (
                <button
                  onClick={() => onOpenConsultation(isHi ? `मेरी वर्तमान ${currentMD?.planet}-${currentAD?.planet} दशा में मेरे लिए सबसे शुभ समय और महत्वपूर्ण निर्णय कब लेना चाहिए?` : `During my active ${currentMD?.planet}-${currentAD?.planet} period, what are the most auspicious months for major decisions?`)}
                  className="w-full py-2 px-3 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-2xs transition-all"
                >
                  <span>{isHi ? 'वर्तमान दशा का पूरा फल पूछें' : 'Ask Full Dasha Forecast'}</span>
                  <ArrowRight className="w-3 h-3 text-astro-accent" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 3. SIMPLIFIED INTERACTIVE CHART WITH PLAIN-LANGUAGE HOUSE EXPLORER */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3 border-b border-astro-border pb-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold font-serif text-astro-text">
              {isHi ? 'सरल जन्म चक्र एवं भाव मार्गदर्शक' : 'Interactive Birth Chart Explorer'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHi ? 'किसी भी भाव (1 से 12) पर क्लिक करें और उसके सरल जीवन अर्थ को जानें' : 'Select any house (1 to 12) below to understand what it means for your life'}
            </p>
          </div>

          {/* Chart Style Switcher */}
          <div className="flex bg-astro-card p-0.5 rounded-lg border border-astro-border text-xs font-medium">
            <button
              onClick={() => setChartStyle('north')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                chartStyle === 'north'
                  ? 'bg-astro-primary text-white font-bold shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'उत्तर भारतीय' : 'North Indian'}
            </button>
            <button
              onClick={() => setChartStyle('south')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                chartStyle === 'south'
                  ? 'bg-astro-primary text-white font-bold shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'दक्षिण भारतीय' : 'South Indian'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          {/* Chart Graphic */}
          <div className="lg:col-span-6 flex justify-center w-full min-w-0">
            <div className="w-full bg-[#FAF6ED] rounded-2xl p-2 sm:p-3 border border-astro-border shadow-inner flex items-center justify-center min-w-0">
              <ResponsiveChartContainer maxDimension={540} minDimension={220} className="w-full min-w-0">
                {() => (
                  chartStyle === 'north' ? (
                    <NorthIndianSvgChart
                      chartData={kundali.charts.D1}
                      planets={kundali.charts.D1.planets}
                      ashtakvarga={kundali.ashtakvarga}
                      selectedHouseNumber={selectedHouse}
                      onSelectHouse={(h) => setSelectedHouse(h)}
                      onSelectPlanet={(p) => p?.house && setSelectedHouse(p.house)}
                      lang={lang}
                      showDegrees={false}
                    />
                  ) : (
                    <SouthIndianSvgChart
                      chartData={kundali.charts.D1}
                      planets={kundali.charts.D1.planets}
                      ashtakvarga={kundali.ashtakvarga}
                      selectedHouseNumber={selectedHouse}
                      onSelectHouse={(h) => setSelectedHouse(h)}
                      onSelectPlanet={(p) => p?.house && setSelectedHouse(p.house)}
                      lang={lang}
                      showDegrees={false}
                    />
                  )
                )}
              </ResponsiveChartContainer>
            </div>
          </div>

          {/* House Detail Inspector Card */}
          <div className="lg:col-span-6 space-y-4">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#FAF6ED] to-astro-bg border border-astro-border shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full">
                  {selectedHouseInfo.focus}
                </span>
                <div className="flex items-center gap-2 text-xs font-bold text-astro-accent flex-wrap">
                  <span>{isHi ? 'राशि:' : 'Sign:'} {getZodiacName(selectedHouseSign, lang)}</span>
                  {selectedHouseLord && (
                    <>
                      <span>•</span>
                      <span>{isHi ? 'स्वामी:' : 'Lord:'} {getPlanetFullName(selectedHouseLord as any, lang)}</span>
                    </>
                  )}
                </div>
              </div>

              <h4 className="text-xl font-bold font-serif text-astro-text">
                {selectedHouseInfo.title}
              </h4>

              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {selectedHouseInfo.desc}
              </p>

              {/* Occupying Planets */}
              <div className="pt-2 border-t border-astro-border/80">
                <div className="text-[11px] font-bold text-astro-primary mb-2">
                  {isHi ? 'इस भाव में स्थित ग्रह:' : 'Planets Sitting in this House:'}
                </div>
                {selectedHousePlanets.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedHousePlanets.map((p, idx) => (
                      <div
                        key={idx}
                        className="px-2.5 py-1 rounded-lg bg-astro-bg border border-astro-border text-xs font-bold text-astro-text shadow-2xs flex items-center gap-1.5"
                      >
                        <span className="text-astro-accent">★</span>
                        <span>{getPlanetFullName(p.name, lang)}</span>
                        <span className="text-[10px] font-normal text-astro-secondary-text">({p.degreeInSign.toFixed(1)}°)</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-astro-secondary-text italic">
                    {isHi ? 'इस भाव में कोई ग्रह प्रत्यक्ष नहीं बैठा है (भाव स्वामी द्वारा संचालित)' : 'No planets sitting directly here (governed purely by sign lord)'}
                  </div>
                )}
              </div>
            </div>

            {/* 12 House Quick Selector Buttons */}
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider">
                {isHi ? 'अन्य भाव चुनें:' : 'Select any House (1-12):'}
              </div>
              <div className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((hNum) => (
                  <button
                    key={hNum}
                    onClick={() => setSelectedHouse(hNum)}
                    className={`py-1.5 rounded-lg text-xs font-bold transition-all ${
                      selectedHouse === hNum
                        ? 'bg-astro-primary text-white shadow-2xs'
                        : 'bg-astro-card text-astro-text hover:bg-astro-border border border-astro-border'
                    }`}
                  >
                    H{hNum}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3.5 CONSOLIDATED CURRENT PHASE FORECAST & 13 LIFE THEMES EXACT EVENT DATES */}
      <div className="pt-2">
        <CurrentPhaseForecastView kundali={kundali} lang={lang} defaultViewMode="layman" />
      </div>

      {/* 4. PRACTICAL DAILY VEDIC REMEDIES & HARMONIZERS */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-center gap-2 border-b border-astro-border pb-3">
          <Sparkles className="w-5 h-5 text-astro-primary" />
          <div>
            <h3 className="text-lg font-bold font-serif text-astro-text">
              {isHi ? 'दैनिक सरल वैदिक उपाय एवं सकारात्मक ऊर्जा' : 'Simple Daily Vedic Upayas & Harmony Rituals'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHi ? 'दैनिक जीवन में सकारात्मक ऊर्जा व बाधा निवारण हेतु आसान उपाय' : 'Practical, authentic practices for peace, vitality, and planetary balance'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
            <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
              <Sun className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? '१. दैनिक प्रातः अर्घ्य' : '1. Morning Solar Offering'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi
                ? 'प्रातःकाल स्नान के पश्चात तांबे के लोटे में जल व रोली लेकर सूर्य देव को अर्घ्य दें। इससे आत्मविश्वास और आरोग्य में वृद्धि होती है।'
                : 'Offer water to the rising Sun in a copper vessel in the morning. This boosts self-confidence, dignity, and metabolic health.'}
            </p>
          </div>

          <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
            <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
              <Moon className="w-4 h-4 text-astro-primary" />
              <span>{isHi ? '२. दैनिक शांति मंत्र' : '2. Daily Peace Mantra'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi
                ? `प्रतिदिन ॐ नमः शिवाय अथवा अपने लग्न स्वामी के मंत्र "${content.mantra}" का १०८ बार जाप करें।`
                : `Chant "${content.mantra}" or "Om Namah Shivaya" 108 times daily for deep mental peace and spiritual alignment.`}
            </p>
          </div>

          <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-2">
            <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? '३. शुभ दान एवं सेवा' : '3. Auspicious Giving (Daan)'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi
                ? `अपने शुभ दिन (${content.luckyDay}) पर जरूरतमंदों को भोजन, पक्षियों को दाना अथवा गाय को हरी घास खिलाएं।`
                : `Feed birds, donate food to the needy, or serve animals on ${content.luckyDay}. This resolves subtle karmic blockages.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
